import moment from "moment";

import { TimeLocale } from "helpers/AppProvider.types";

import { type IJobParamGridRequestData, type IUserPermissions } from "utilities/list/list.types";

import { type IMapPosition } from "commonComponents/entity/map/Map.types";
import { formatDate } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { GridEntity, type IGridRowData } from "commonComponents/utilities/Grid/GridContainer.types";

import type { default as ShiftListApiJson } from "entity/timeClock/ShiftList/ShiftList.api.json";
import type { default as ShiftListApiJsonWithActionCol } from "entity/timeClock/ShiftList/ShiftListWithActionCol.api.json";

export type ShiftListFormActions =
    | "approveShifts"
    | "sendToQuickbooks"
    | "checkedActions"
    | undefined;

export enum ShiftGroupBy {
    None = 0,
    Employee = 1,
    Job = 2,
    Date = 3,
    "Cost Code" = 4,
    Approval = 5,
}

export interface ITimeClockPermissions {
    basicPermissions: IUserPermissions;
    viewOtherUserTimeSheets: boolean;
    adjustOtherUserTimeSheets: boolean;
    reviewAndApproveUserShifts: boolean;
    sendToAccounting: boolean;
    autoSendToAccounting: boolean;
    costViewing: boolean;
    updateOwnerInvoice: boolean;
    canCreateOwnerInvoice: boolean;
}

interface IShiftMapInfo {
    userName: string;
    timestamp: string;
    location: { latitude: number; longitude: number };
}

interface IShiftCostCode {
    id: number;
    costCodeTitle: string;
    totalMinutes: number;
    useDecimal: boolean;
}

export interface IShiftListRow extends IGridRowData {
    jobId: number | undefined;
    userID: number;
    jobName: string;
    name: string;
    employeeGroup: string;
    jobGroup: string;
    // grouping key formatted as (ddd, MMM D, YYYY)
    shiftDate: string;
    approved: string;
    firstName: string;
    lastName: string;
    date: {
        date: string;
        builderId: number;
        jobId: number | null;
        id: number;
    };
    timeIn: {
        timeStamp: string | null;
        location: { latitude: number | null; longitude: number | null } | null;
        outOfBounds: boolean;
    };
    timeOut: {
        timeStamp: string | null;
        location: { latitude: number | null; longitude: number | null } | null;
        outOfBounds: boolean;
        canClockOut: boolean;
    };
    regularTime: number | { totalMinutes: number };
    overTime: number | { totalMinutes: number };
    doubleOverTime: number | { totalMinutes: number };
    personalTimeOff: number | { totalMinutes: number };
    totalWorkTime: number | { totalMinutes: number };
    // currency columns (server type "currency", shape { value, scale })
    builderCost: { value: number | null; scale: number } | null;
    billableAmount: { value: number | null; scale: number } | null;
    breakTime: {
        breakTime: number;
    };
    approvalStatus: {
        isApproved: boolean;
    };
    costCodes: IShiftCostCode[] | null;
    costCode: string | null;
    locationInfo: {
        jobName: string;
        clockInInfo: IShiftMapInfo | null;
        clockOutInfo: IShiftMapInfo | null;
    } | null;
}

export class ShiftMapData {
    constructor(
        clockOut: boolean,
        jobName: string,
        info: IShiftMapInfo,
        outOfBounds: boolean,
        shiftId: number,
        jobId: number | undefined,
    ) {
        this.clockOut = clockOut;
        this.jobName = jobName;
        this.userName = info.userName;
        this.timestamp = moment(info.timestamp);
        this.position = {
            lat: info.location.latitude,
            lng: info.location.longitude,
        };
        this.outOfBounds = outOfBounds;
        this.shiftId = shiftId;
        this.jobId = jobId;
    }
    clockOut: boolean;
    jobName: string;
    userName: string;
    timestamp: moment.Moment;
    position: IMapPosition;
    outOfBounds: boolean;
    shiftId: number;
    jobId: number | undefined;
}

type ShiftGridEntityArgs = Omit<
    typeof ShiftListApiJson | typeof ShiftListApiJsonWithActionCol,
    "invoiceableTimeCardIds"
> & {
    invoiceableTimeCardIds: number[];
};
export class ShiftGridEntity extends GridEntity<object, IShiftListRow> {
    constructor(data: ShiftGridEntityArgs) {
        super(data);
        this.useDecimal = data.useDecimal;
        this.permissions = data.permissions;
        this.hasGustoAccess = data.hasGustoAccess;
        this.invoiceableTimeCardIds = data.invoiceableTimeCardIds;
        this.hasEligibleOwnerInvoices = data.hasEligibleOwnerInvoices;

        if (data.jobLocation) {
            this.jobLocation = {
                lat: data.jobLocation.latitude,
                lng: data.jobLocation.longitude,
            };
        }

        this.mapItems = [];
        this.data.forEach((value) => {
            value.employeeGroup = value.name;
            value.jobGroup = value.jobName;
            value.shiftDate = value.date?.date
                ? formatDate(moment(value.date.date), TimeLocale.default, {
                      showDate: true,
                      showTime: false,
                      showDayOfWeek: true,
                      showMonth: true,
                      showYear: true,
                  })
                : "No Date";
            value.costCode =
                value.costCodes && value.costCodes.length > 0
                    ? value.costCodes
                          .map((c) => c.costCodeTitle)
                          .sort()
                          .join(" / ")
                    : null;
            if (value.approvalStatus.isApproved) {
                value.approved = "Approved";
            } else {
                value.approved = "Pending";
            }
            if (value.locationInfo) {
                if (value.locationInfo.clockInInfo?.location) {
                    this.mapItems.push(
                        new ShiftMapData(
                            false,
                            value.locationInfo.jobName,
                            value.locationInfo.clockInInfo,
                            value.timeIn.outOfBounds,
                            value.id,
                            value.jobId,
                        ),
                    );
                }
                if (value.locationInfo.clockOutInfo?.location) {
                    this.mapItems.push(
                        new ShiftMapData(
                            true,
                            value.locationInfo.jobName,
                            value.locationInfo.clockOutInfo,
                            value.timeOut.outOfBounds,
                            value.id,
                            value.jobId,
                        ),
                    );
                }
            }
        });
    }

    useDecimal: boolean;
    permissions: ITimeClockPermissions;
    jobLocation?: IMapPosition;
    mapItems: ShiftMapData[];
    hasGustoAccess: boolean;
    invoiceableTimeCardIds: number[];
    hasEligibleOwnerInvoices: boolean;
}

export interface IShiftGridRequestData extends IJobParamGridRequestData {
    includeGeneralJobItems: boolean;
}
